import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { newStreamToken, stopStream, streamUrl } from "../lib/api";
import { GestureRecognizer, type DragPayload, type TapPayload } from "../lib/gesture";
import { A11yOverlay } from "./A11yOverlay";
import type { UIElement } from "../types";

const ORIENTATION_POLL_MS = 500;

interface Props {
  deviceId: string;
  deviceWidth: number;
  deviceHeight: number;
  fps: number;
  elements: UIElement[];
  onTap: (p: TapPayload) => void;
  onDrag: (p: DragPayload) => void;
  onStreamError?: () => void;
  onOrientationChange?: (isLandscape: boolean) => void;
}

export function DeviceFrame({
  deviceId,
  deviceWidth,
  deviceHeight,
  fps,
  elements,
  onTap,
  onDrag,
  onStreamError,
  onOrientationChange,
}: Props) {
  const frameRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Stream-derived orientation. The server's screenWidth/Height is reported
  // in native (portrait) orientation, so when the device is rotated the
  // MJPEG frame is landscape while deviceWidth/deviceHeight are still
  // portrait. We flip once we see the first frame's natural dims.
  const [streamLandscape, setStreamLandscape] = useState<boolean | null>(null);

  const shortEdge = Math.min(deviceWidth, deviceHeight);
  const longEdge = Math.max(deviceWidth, deviceHeight);
  const isLandscape = streamLandscape ?? deviceWidth > deviceHeight;
  const frameW = isLandscape ? longEdge : shortEdge;
  const frameH = isLandscape ? shortEdge : longEdge;

  // Send taps/drags in the current visual orientation — the server's
  // iOS/WDA client rotates to native portrait internally, and Android's
  // tap injection already uses visual coords, so this is the single
  // coordinate system every bridge now speaks.
  const recognizer = useMemo(
    () =>
      new GestureRecognizer(
        (e) => {
          const el = imgRef.current;
          if (!el) return { x: 0, y: 0 };
          const r = el.getBoundingClientRect();
          return {
            x: Math.round(((e.clientX - r.left) * frameW) / r.width),
            y: Math.round(((e.clientY - r.top) * frameH) / r.height),
          };
        },
        onDrag,
        onTap,
      ),
    [frameW, frameH, onDrag, onTap],
  );

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const capture = (id: number) => {
      try {
        frame.setPointerCapture(id);
      } catch {
        /* no-op */
      }
    };
    const down = (e: PointerEvent) => recognizer.onPointerDown(e);
    const move = (e: PointerEvent) => recognizer.onPointerMove(e, capture);
    const up = (e: PointerEvent) => recognizer.onPointerUp(e);
    const cancel = () => recognizer.onPointerCancel();
    const click = () => recognizer.onClick();
    frame.addEventListener("pointerdown", down);
    frame.addEventListener("pointermove", move);
    frame.addEventListener("pointerup", up);
    frame.addEventListener("pointercancel", cancel);
    frame.addEventListener("click", click);
    return () => {
      frame.removeEventListener("pointerdown", down);
      frame.removeEventListener("pointermove", move);
      frame.removeEventListener("pointerup", up);
      frame.removeEventListener("pointercancel", cancel);
      frame.removeEventListener("click", click);
    };
  }, [recognizer]);

  // Build stream URL imperatively so we own the lifecycle: on deps
  // change / unmount we fire a DELETE to the server-side stop endpoint.
  // WebKit won't close the in-flight multipart response for us, and the
  // URL needs a fresh token anyway so the two don't race.
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    const token = newStreamToken();
    setSrc(streamUrl(deviceId, fps, token));
    return () => {
      stopStream(deviceId, token);
    };
  }, [deviceId, fps]);

  // Poll the visible MJPEG image's natural dimensions so mid-session
  // rotations flip the frame.
  useEffect(() => {
    const id = window.setInterval(() => {
      const img = imgRef.current;
      if (!img) return;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (w === 0 || h === 0) return;
      const next = w > h;
      setStreamLandscape((prev) => {
        if (prev === next) return prev;
        onOrientationChange?.(next);
        return next;
      });
    }, ORIENTATION_POLL_MS);
    return () => window.clearInterval(id);
  }, [onOrientationChange]);

  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [erroredSrc, setErroredSrc] = useState<string | null>(null);
  const loaded = loadedSrc === src;
  const errored = erroredSrc === src && !loaded;

  return (
    <div className="w-full h-full flex items-center justify-center [container-type:size]">
      <div
        ref={frameRef}
        id="frame"
        className="relative bg-black rounded-2xl overflow-hidden shadow-2xl select-none cursor-crosshair max-w-full max-h-full"
        style={{
          aspectRatio: `${frameW} / ${frameH}`,
          // Width = min(container width, container height × aspect).
          // Height derives from aspect-ratio. Pure CSS, no observer.
          width: `min(100cqw, calc(100cqh * ${frameW} / ${frameH}))`,
          touchAction: "none",
        }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {src && (
          <img
            ref={imgRef}
            src={src}
            alt="device screen"
            draggable={false}
            className="w-full h-full object-contain pointer-events-none block"
            onLoad={(e) => {
              const img = e.currentTarget;
              if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                const next = img.naturalWidth > img.naturalHeight;
                setStreamLandscape((prev) => {
                  if (prev === next) return prev;
                  onOrientationChange?.(next);
                  return next;
                });
              }
              setLoadedSrc(src);
            }}
            onError={() => {
              setErroredSrc(src);
              onStreamError?.();
            }}
          />
        )}
        {!loaded && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-surface-900/70 backdrop-blur-sm pointer-events-none">
            {errored ? (
              <>
                <div className="text-sm text-rose-300">Stream error</div>
                <div className="mt-1 text-xs text-surface-400">Retrying…</div>
              </>
            ) : (
              <>
                <Loader2 className="w-6 h-6 text-accent-cyan animate-spin" />
                <div className="mt-2 text-xs text-surface-400">
                  Starting stream… (bridge may be booting)
                </div>
              </>
            )}
          </div>
        )}
        <A11yOverlay
          elements={elements}
          deviceWidth={frameW}
          deviceHeight={frameH}
        />
      </div>
    </div>
  );
}
